import { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Box,
    Button,
    CircularProgress,
    Alert,
    TextField,
    Tabs,
    Tab,
    List,
    ListItem,
    Divider
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { updateTitleDescription, fetchComponentHistory } from "../utils/api";

const ExpandableText = ({ text, maxLength = 100 }) => {
    const [expanded, setExpanded] = useState(false);
    
    if (!text) return <span>N/A</span>;
    if (text.length <= maxLength) return <span>{text}</span>;
    
    return (
        <span>
            {expanded ? text : `${text.substring(0, maxLength)}...`}
            <Button 
                variant="text" 
                size="small" 
                onClick={() => setExpanded(!expanded)}
                sx={{ textTransform: 'none', p: 0, ml: 1, minWidth: 'auto' }}
            >
                {expanded ? 'See less' : 'See more'}
            </Button>
        </span>
    );
};

const ResourceJsonEditorPopup = ({
    open,
    handleClose,
    componentName,
    version,
    title: propTitle = "",
    description: propDescription = "",
    onSaveSuccess,
}) => {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [saveLoading, setSaveLoading] = useState(false);
    const [error, setError] = useState("");
    const [tabValue, setTabValue] = useState(0);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setTitle(propTitle || "");
            setDescription(propDescription || "");
            setError("");
            setTabValue(0);

            const getHistory = async () => {
                setHistoryLoading(true);
                try {
                    const data = await fetchComponentHistory(componentName);
                    if (data && data.history) {
                        const filtered = data.history.filter(h => h.component_status === 'titleDescriptionUpdated' || h.component_status === 'titleDescriptionUpdatedOnDev');
                        setHistory(filtered);
                    }
                } catch (err) {
                    console.error("Failed to fetch history:", err);
                } finally {
                    setHistoryLoading(false);
                }
            };
            getHistory();
        }
    }, [open, propTitle, propDescription, componentName]);

    // Clear state when dialog closes
    useEffect(() => {
        if (!open) {
            setTitle("");
            setDescription("");
            setError("");
        }
    }, [open]);

    // ─── Save handler ─────────────────────────────────────────────────────────
    const handleSave = async () => {
        setSaveLoading(true);
        setError("");
        try {
            await updateTitleDescription(componentName, title, description);
            if (onSaveSuccess) onSaveSuccess("Updated successfully!");
            handleClose();
        } catch (err) {
            setError(err.message || "Failed to save");
        } finally {
            setSaveLoading(false);
        }
    };

    const isSaveDisabled = saveLoading || (!title.trim() && !description.trim());

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: 2, p: 0.5 } }}
        >
            {/* ── Title ─────────────────────────────────────────────────────── */}
            <DialogTitle sx={{ fontWeight: 700, fontSize: "18px", pr: 6 }}>
                Edit Title & Description
                <Typography
                    component="span"
                    variant="body2"
                    sx={{ ml: 1.5, color: "text.secondary", fontWeight: 400 }}
                >
                    {componentName}{version ? `@${version}` : ""}
                </Typography>
                <IconButton
                    onClick={handleClose}
                    sx={{ position: "absolute", right: 8, top: 8 }}
                    disabled={saveLoading}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* ── Content ───────────────────────────────────────────────────── */}
            <DialogContent dividers sx={{ p: 2, minHeight: "300px" }}>
                <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)} sx={{ mb: 2 }}>
                    <Tab label="Edit" />
                    <Tab label="History" />
                </Tabs>

                {error && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                        {error}
                    </Alert>
                )}

                {tabValue === 0 && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1 }}>
                        <TextField
                            label="Title"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                        <TextField
                            label="Description"
                            variant="outlined"
                            size="small"
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={6}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </Box>
                )}

                {tabValue === 1 && (
                    <Box sx={{ py: 1 }}>
                        {historyLoading ? (
                            <CircularProgress size={24} />
                        ) : history.length > 0 ? (
                            <List sx={{ maxHeight: "300px", overflow: "auto" }}>
                                {history.map((h, i) => (
                                    <Box key={i}>
                                        <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                {h.action_date ? new Date(h.action_date).toLocaleString() : ""} {h.reviewer_email ? `by ${h.reviewer_email}` : ""}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                <strong>Title:</strong> <ExpandableText text={h.Title} maxLength={100} />
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Description:</strong> <ExpandableText text={h.Description} maxLength={150} />
                                            </Typography>
                                        </ListItem>
                                        {i < history.length - 1 && <Divider />}
                                    </Box>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No history found.
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>

            {/* ── Actions ───────────────────────────────────────────────────── */}
            <DialogActions sx={{ px: 2.5, py: 1.5 }}>
                <Button
                    onClick={handleClose}
                    disabled={saveLoading}
                    variant="outlined"
                    color="inherit"
                    sx={{ textTransform: "none", borderRadius: "20px" }}
                >
                    Cancel
                </Button>
                {tabValue === 0 && (
                    <Button
                        onClick={handleSave}
                        disabled={isSaveDisabled}
                        variant="contained"
                        color="primary"
                        startIcon={
                            saveLoading ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <SaveIcon />
                            )
                        }
                        sx={{ textTransform: "none", borderRadius: "20px" }}
                    >
                        {saveLoading ? "Saving…" : "Save"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ResourceJsonEditorPopup;

